import React, { useEffect, useState } from "react";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core";
import StatTile, { formatCompact, formatCount } from "../StatTile";
import WarningBanner from "../WarningBanner";
import HorizontalBarChart from "../charts/HorizontalBarChart";
import TrendLineChart from "../charts/TrendLineChart";
import { getMaterialSpendCostSpike } from "../../../services/metricsApi";
import BaselineSelect, { DEFAULT_BASELINE } from "./BaselineSelect";
import "../Analytics.css";

export const DEFAULT_SPIKE_FILTERS = { baseline: DEFAULT_BASELINE, currentPricePeriod: "" };

const CURRENT_PRICE_OPTIONS = [
  { value: "", label: "Follow trend filter" },
  { value: "MTD", label: "MTD" },
  { value: "QUARTERLY", label: "QUARTERLY" },
  { value: "YTD", label: "YTD" },
];

const CURRENT_PRICE_TOOLTIP =
  "Only changes Current Avg Price, variance and $ impact. Spend, counts and the Pareto still follow the trend filter.";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Parses "YYYY-MM-DD" by hand; new Date(str) would shift the day in negative-UTC zones.
const parseIsoDate = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  return { y, m, d };
};
const formatMonthYear = (iso) => {
  const { y, m } = parseIsoDate(iso);
  return `${MONTHS[m - 1]} ${y}`;
};
const formatDayMonth = (iso) => {
  const { m, d } = parseIsoDate(iso);
  return `${d} ${MONTHS[m - 1]}`;
};

const baselineCaption = (baseline) => {
  const ranges = baseline?.ranges || [];
  if (ranges.length === 0) return null;
  const labels = ranges.map((r) => r.label).join(" + ");
  const from = ranges.map((r) => r.from).sort()[0];
  const to = ranges.map((r) => r.to).sort().slice(-1)[0];
  return `Baseline: ${labels} (${formatMonthYear(from)} – ${formatMonthYear(to)})`;
};

const currentPriceCaption = (currentPrice) =>
  currentPrice?.period && currentPrice.from && currentPrice.to
    ? `Current avg: ${currentPrice.period} (${formatDayMonth(currentPrice.from)} – ${formatDayMonth(currentPrice.to)})`
    : null;

const SPIKE_TABLE_COLUMNS = (groupLabel) => [
  { id: "group", label: groupLabel },
  { id: "current_count", label: "Count" },
  { id: "current_spend", label: "Current Spend (USD)" },
  { id: "current_avg_unit_price", label: "Current Avg Price" },
  { id: "baseline_avg_unit_price", label: "Baseline Avg Price" },
  { id: "variance_pct", label: "Variance %" },
  { id: "is_spike", label: "Spike?" },
  { id: "dollar_impact", label: "$ Impact" },
];

const GROUPINGS = [
  { key: "by_material", label: "By Material", groupField: "material" },
  { key: "by_plant", label: "By Plant", groupField: "plant" },
  { key: "by_mrs_classification", label: "By MRS Classification", groupField: "mrs_classification" },
];

const toTableRow = (row, groupField) => ({
  _id: `${row[groupField]}`,
  group: row[groupField],
  current_count: formatCount(row.current_count),
  current_spend: formatCompact(row.current_spend, { currency: true }),
  current_avg_unit_price: formatCompact(row.current_avg_unit_price, { currency: true }),
  baseline_avg_unit_price: row.baseline_avg_unit_price != null ? formatCompact(row.baseline_avg_unit_price, { currency: true }) : "–",
  variance_pct: row.insufficient_history
    ? "Insufficient history"
    : row.variance_pct != null ? `${Number(row.variance_pct).toFixed(1)}%` : "–",
  is_spike: row.is_spike ? "⚠ Spike" : row.insufficient_history ? "–" : "No",
  dollar_impact: row.dollar_impact != null ? formatCompact(row.dollar_impact, { currency: true }) : "–",
});

const CONTRIBUTOR_COLUMNS = [
  { id: "material", label: "Material" },
  { id: "current_avg_unit_price", label: "Current Avg Price" },
  { id: "baseline_avg_unit_price", label: "Baseline Avg Price" },
  { id: "variance_pct", label: "Variance %" },
  { id: "dollar_impact", label: "$ Impact" },
];

const toContributorRow = (row) => ({
  _id: `${row.material}`,
  material: row.material,
  current_avg_unit_price: formatCompact(row.current_avg_unit_price, { currency: true }),
  baseline_avg_unit_price: row.baseline_avg_unit_price != null ? formatCompact(row.baseline_avg_unit_price, { currency: true }) : "–",
  variance_pct: row.variance_pct != null ? `${Number(row.variance_pct).toFixed(1)}%` : "–",
  dollar_impact: row.dollar_impact != null ? formatCompact(row.dollar_impact, { currency: true }) : "–",
});

// §8 Material Spend & Cost Spike Analysis — one spike-detection engine (current
// avg USD-normalized unit price vs. a selectable baseline; threshold comes from
// the response) sliced 3 ways: material / plant / MRS classification.
const MaterialSpendCostSpikeSection = ({ id, filters, spikeFilters = DEFAULT_SPIKE_FILTERS, onSpikeFiltersChange, refreshKey }) => {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [activeGrouping, setActiveGrouping] = useState(GROUPINGS[0].key);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    getMaterialSpendCostSpike({
      ...filters,
      baseline: spikeFilters.baseline,
      current_price_period: spikeFilters.currentPricePeriod,
    })
      .then((data) => {
        if (!cancelled) setState({ loading: false, data, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, data: null, error: err.message });
      });

    return () => {
      cancelled = true;
    };
  }, [filters, spikeFilters, refreshKey]);

  const { loading, data, error } = state;
  const pareto = data?.pareto || { materials: [] };
  const repeatFrequency = data?.repeat_frequency || { top_repeat_materials: [] };
  const costSpike = data?.cost_spike || {};
  const criticalMaterials = data?.critical_materials || [];
  const spikeThresholdPct = data?.spike_threshold_pct;
  const baselineText = baselineCaption(data?.baseline);
  const currentPriceText = currentPriceCaption(data?.current_price);
  const updateSpikeFilters = (patch) => onSpikeFiltersChange?.({ ...spikeFilters, ...patch });

  const paretoItems = (pareto.materials || []).map((m) => ({
    key: m.material,
    label: m.material,
    value: m.current_spend || 0,
  }));

  const repeatItems = (repeatFrequency.top_repeat_materials || []).map((m) => ({
    key: m.material,
    label: m.material,
    value: m.count || 0,
  }));

  const activeConfig = GROUPINGS.find((g) => g.key === activeGrouping) || GROUPINGS[0];
  const activeRows = costSpike[activeGrouping] || [];

  const varianceItems = activeRows
    .filter((r) => !r.insufficient_history && r.variance_pct != null)
    .map((r) => ({
      key: r[activeConfig.groupField],
      label: r[activeConfig.groupField],
      value: r.variance_pct,
    }));

  const activeTableData = activeRows.map((r) => toTableRow(r, activeConfig.groupField));

  const allRows = [costSpike.by_material, costSpike.by_plant, costSpike.by_mrs_classification].flat().filter(Boolean);
  const noBaselineData = allRows.length > 0 && allRows.every((r) => r.insufficient_history);
  const topContributors = costSpike.top_contributors || [];
  const trendSeries = [
    {
      key: "current",
      label: "Current Avg Unit Price",
      values: topContributors.map((m) => Number(m.current_avg_unit_price) || 0),
    },
    {
      key: "baseline",
      label: "Baseline Avg Unit Price",
      values: topContributors.map((m) => Number(m.baseline_avg_unit_price) || 0),
    },
  ];
  const trendLabels = topContributors.map((m) => m.material);

  return (
    <section id={id} className="analytics-section">
      <div className="analytics-section-header">
        <h2 className="analytics-section-heading">Material Spend &amp; Cost Spike</h2>
        <div className="spike-filters">
          <BaselineSelect value={spikeFilters.baseline} onChange={(baseline) => updateSpikeFilters({ baseline })} />
          <label className="spike-filter" title={CURRENT_PRICE_TOOLTIP}>
            <span className="spike-filter-label">Current Average Price ⓘ</span>
            <select
              className="spike-filter-trigger"
              value={spikeFilters.currentPricePeriod}
              onChange={(e) => updateSpikeFilters({ currentPricePeriod: e.target.value })}
              aria-describedby="spike-current-price-hint"
            >
              {CURRENT_PRICE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <span id="spike-current-price-hint" className="spike-visually-hidden">{CURRENT_PRICE_TOOLTIP}</span>
          </label>
        </div>
      </div>
      <div className="spike-captions">
        {spikeThresholdPct !== undefined && (
          <span className="analytics-section-caption">
            Spike = current avg unit price &gt;{spikeThresholdPct}% above baseline
          </span>
        )}
        {baselineText && <span className="analytics-section-caption">{baselineText}</span>}
        {currentPriceText && <span className="analytics-section-caption">{currentPriceText}</span>}
      </div>

      <WarningBanner warning={data?.warning} />
      {error && <div className="overview-card-error">Failed to load Material Spend &amp; Cost Spike: {error}</div>}
      {loading && !data && <div className="overview-card-loading">Loading…</div>}

      {data && (
        <>
          {(noBaselineData || (data.baseline?.ranges || []).length === 0) && (
            <div className="analytics-warning-banner" role="status">
              No data in the selected baseline. Try a different baseline.
            </div>
          )}
          <div className="stat-tile-row">
            <StatTile label="Total Spend (USD)" value={formatCompact(pareto.total_spend, { currency: true })} />
            <StatTile label="Total Materials" value={formatCount(pareto.total_material_count)} />
            <StatTile label="Top 10 Spend Share" value={pareto.top_group_spend_pct !== undefined ? `${Number(pareto.top_group_spend_pct).toFixed(1)}%` : "–"} />
            <StatTile label="Critical Materials" value={formatCount(criticalMaterials.length)} status={criticalMaterials.length > 0 ? "critical" : undefined} />
          </div>

          <div className="analytics-charts-row">
            <div className="analytics-section-card">
              <h4 className="analytics-section-title">Pareto — Spend by Material</h4>
              <span className="analytics-section-caption">
                Top {pareto.top_count} of {pareto.total_material_count} materials &middot; {pareto.top_group_spend_pct !== undefined ? Number(pareto.top_group_spend_pct).toFixed(1) : "–"}% of spend
              </span>
              <HorizontalBarChart
                items={paretoItems}
                valueFormatter={(v) => formatCompact(v, { currency: true })}
                maxItems={10}
              />
            </div>

            <div className="analytics-section-card">
              <h4 className="analytics-section-title">Most Frequently Charged Materials</h4>
              <HorizontalBarChart items={repeatItems} valueFormatter={(v) => formatCount(v)} maxItems={10} />
            </div>
          </div>

          <div className="analytics-section-card">
            <h4 className="analytics-section-title">Current vs Baseline — Top Contributors</h4>
            <TrendLineChart
              series={trendSeries}
              xLabels={trendLabels}
              valueFormatter={(v) => formatCompact(v, { currency: true })}
            />
            {topContributors.length > 0 ? (
              <McTable
                data={topContributors.map(toContributorRow)}
                datakey="_id"
                zebrastripes
                truncate
                fit="small"
                columns={CONTRIBUTOR_COLUMNS}
              />
            ) : (
              <div className="hbar-empty">No spiking materials with a positive dollar impact.</div>
            )}
          </div>

          <div className="analytics-section-card">
            <h4 className="analytics-section-title">Cost Spike &amp; Variance Analysis</h4>

            <div className="tl-tab-strip" role="tablist">
              {GROUPINGS.map((g) => (
                <button
                  key={g.key}
                  type="button"
                  role="tab"
                  aria-selected={g.key === activeGrouping}
                  className={`tl-tab${g.key === activeGrouping ? " tl-tab-active" : ""}`}
                  onClick={() => setActiveGrouping(g.key)}
                >
                  {g.label}
                </button>
              ))}
            </div>

            <HorizontalBarChart
              items={varianceItems}
              valueFormatter={(v) => `${Number(v).toFixed(1)}%`}
              maxItems={15}
              threshold={spikeThresholdPct !== undefined ? { value: spikeThresholdPct, label: `Spike: >${spikeThresholdPct}%` } : undefined}
            />

            <details className="analytics-collapsible" open={activeTableData.length <= 8}>
              <summary>{activeConfig.label} — Full breakdown — {activeTableData.length} rows</summary>
              <McTable data={activeTableData} datakey="_id" zebrastripes truncate fit="small" columns={SPIKE_TABLE_COLUMNS(activeConfig.label.replace("By ", ""))} />
            </details>
          </div>

          <div className="analytics-section-card critical-materials-card">
            <h4 className="analytics-section-title">Critical Materials — Top 10 by Spend &amp; Spiking</h4>
            {criticalMaterials.length > 0 ? (
              <McTable
                data={criticalMaterials.map((m) => toTableRow(m, "material"))}
                datakey="_id"
                zebrastripes
                truncate
                fit="small"
                columns={SPIKE_TABLE_COLUMNS("Material")}
              />
            ) : (
              <div className="hbar-empty">No materials are both in the top 10 by spend and currently spiking.</div>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default MaterialSpendCostSpikeSection;
